import { BehaviorSubject, combineLatest, of } from "rxjs";
import { distinctUntilChanged, map, switchMap } from "rxjs/operators";

import { presentations as presentationsApi, slides as slidesApi } from "apis-v2/callables";
import { isOwnerOrLibrarian } from "common/utils/roles";
import Api from "js/core/api";
import getLogger, { LogGroup } from "js/core/logger";
import pusher from "js/core/services/pusher";
import db from "js/db";
import { app } from "js/namespaces";

const logger = getLogger(LogGroup.OBSERVABLES);

// Using this as a static entity for caching user profiles
const userProfiles = {};

const getObservableMock = () => ({
    subscribe: () => ({ unsubscribe: () => { } })
});

export class Observables {
    constructor(dataService) {
        // Avoiding circular dependency
        this.dataService = dataService;

        this._slideStatus$ = null;
        this._assignedToSlideUser$ = null;
        this._collaborators$ = null;
    }

    get slideStatus$() {
        if (!this._slideStatus$) {
            this._slideStatus$ = window.mockObservables ? getObservableMock() : this._getSlideStatusObservable();
        }

        return this._slideStatus$;
    }

    get assignedToSlideUser$() {
        if (!this._assignedToSlideUser$) {
            this._assignedToSlideUser$ = window.mockObservables ? getObservableMock() : this._getAssignedToSlideUserObservable();
        }

        return this._assignedToSlideUser$;
    }

    get collaborators$() {
        if (!this._collaborators$) {
            this._collaborators$ = window.mockObservables ? getObservableMock() : this._getCollaboratorsObservable();
        }

        return this._collaborators$;
    }

    _getSlideStatusObservable() {
        // Subject and observable, will be starting from null
        const _slideStatus$ = new BehaviorSubject(null);
        const slideStatus$ = _slideStatus$.asObservable();

        let currentSlide;
        const onSelectionChanged = selection => {
            const slide = selection.slide;
            if (slide === currentSlide) {
                return;
            }

            // Unsubscribing from current slide
            if (currentSlide) {
                currentSlide.off("change", onSlideChanged);
            }
            currentSlide = slide;

            if (!slide) {
                _slideStatus$.next(null);
                return;
            }

            // The player doesn't allow listening, so we skip listening to the slide status
            if (slide.on) {
                // Subscribing for new slide
                slide.on("change", onSlideChanged);

                // Triggering onSlideChanged() to set current slide status value
                onSlideChanged(slide);
            }
        };
        const onSlideChanged = async slide => {
            try {
                // Make sure the model loaded
                await slide.loadPromise;
                // Wait for the update to propagate to the db
                // (if triggered by a local update)
                if (slide.updatePromise) {
                    await slide.updatePromise;
                }

                if (currentSlide.id !== slide.id) {
                    return;
                }

                const slideStatus = slide.get("status");
                if (slideStatus) {
                    _slideStatus$.next(slideStatus);
                } else {
                    _slideStatus$.next(null);
                }
            } catch (err) {
                logger.error(err, "_getSlideStatusObservable() onSlideChanged() failed");
            }
        };

        this.dataService.selection.on("change:slide", onSelectionChanged);
        this.dataService.selection.on("firstSlideSet", () => onSelectionChanged(this.dataService.selection));

        // Fetching the initial state
        onSelectionChanged(this.dataService.selection);

        return slideStatus$;
    }

    _getAssignedToSlideUserObservable() {
        // Subject and observable, will be starting from null
        const _assignedToSlideUser$ = new BehaviorSubject(null);
        const assignedToSlideUser$ = _assignedToSlideUser$.asObservable();

        let currentSlide;
        const onSelectionChanged = selection => {
            const slide = selection.slide;
            if (slide === currentSlide) {
                return;
            }

            // Unsubscribing from current slide
            if (currentSlide) {
                currentSlide.off("change", onSlideChanged);
            }
            currentSlide = slide;

            if (!slide) {
                _assignedToSlideUser$.next(null);
                return;
            }

            // The player doesn't allow listening, so we skip the loading assignees
            if (slide?.on) {
                // Subscribing for new slide
                slide.on("change", onSlideChanged);

                // Triggering onSlideChanged() to set current assigned user value
                onSlideChanged(slide);
            }
        };
        const onSlideChanged = async slide => {
            try {
                // Make sure the model loaded
                await slide.loadPromise;
                // Wait for the update to propagate to the db
                // (if triggered by a local update)
                if (slide.updatePromise) {
                    // No error logging here, we're just waiting for the update to propagate
                    await slide.updatePromise.catch(() => { });
                }

                const assignedUserUid = slide.get("assignedUser");
                const pendingUserEmail = slide.get("assignedPendingUser");

                if (assignedUserUid) {
                    if (assignedUserUid === app.user.id) {
                        // Optimization to avoid calling server to get current user
                        _assignedToSlideUser$.next(app.user.getAuthUser());
                    } else if (userProfiles[assignedUserUid]) {
                        // Found in cache
                        _assignedToSlideUser$.next(userProfiles[assignedUserUid]);
                    } else {
                        const user = await slidesApi.getSlideOwner({
                            presentationId: this.dataService.selection.presentation.id,
                            id: slide.id
                        });

                        // Caching
                        userProfiles[assignedUserUid] = user;

                        _assignedToSlideUser$.next(user);
                    }
                } else if (pendingUserEmail) {
                    _assignedToSlideUser$.next({ email: pendingUserEmail, isPending: true });
                } else {
                    _assignedToSlideUser$.next(null);
                }
            } catch (err) {
                logger.error(err, "_getAssignedToSlideUserObservable() onSlideChanged() failed");
            }
        };

        this.dataService.selection.on("change:slide", onSelectionChanged);
        this.dataService.selection.on("firstSlideSet", () => onSelectionChanged(this.dataService.selection));

        // Fetching the initial state
        onSelectionChanged(this.dataService.selection);

        return assignedToSlideUser$;
    }

    _getCollaboratorsObservable() {
        // Subject and observable, will be starting from null
        const _collaborators$ = new BehaviorSubject({});
        const collaborators$ = _collaborators$.asObservable();

        let pusherChannel;
        const leaveChannel = () => {
            pusherChannel.unbind_all();
            pusher.unsubscribe(pusherChannel.name);
            pusherChannel = null;
        };

        // This will be storing the current collaborators state
        let collaborators = {};

        const onSlideChanged = slide => {
            if (!pusherChannel) {
                return;
            }

            // Let everybody know the slide we're on
            pusherChannel.trigger("client-on-slide", { slideId: slide?.id });
            // Trigger refresh to allow the collaboration view to refresh on slide changes
            _collaborators$.next(collaborators);
        };

        let currentPresentationId;
        const onPresentationChanged = async presentation => {
            // No presentation or slide, or presentation is dummy, ignore
            if (!presentation || presentation.get("isDummy") === true) {
                // Leave current channel
                if (pusherChannel) {
                    leaveChannel();
                }
                // Set empty state
                collaborators = {};
                _collaborators$.next(collaborators);
                return;
            }

            try {
                // Presentation changed or there's no channel, then subscribe to channel
                if (currentPresentationId !== presentation.id || !pusherChannel) {
                    // Set empty state, will report below
                    collaborators = {};

                    if (pusherChannel) {
                        // The existing channel listens to another presentation, leave the channel
                        leaveChannel();
                    }

                    // Making sure we have write permissions
                    await presentation.getUserPermissions(true);
                    if (!presentation.permissions.write) {
                        _collaborators$.next(collaborators);
                        return;
                    }

                    // Connect to new channel and binding events
                    pusherChannel = await pusher.subscribe(`presence-presentation-collaboration-${presentation.id}`);
                    if (!pusherChannel) {
                        // Didn't subscribe (pusher is disabled)
                        _collaborators$.next(collaborators);
                        return;
                    }

                    pusherChannel.bind("pusher:member_added", ({ id: uid, info: user }) => {
                        // Let new user know which slide we're on
                        pusherChannel.trigger("client-on-slide", { slideId: this.dataService.selection.slide?.id });

                        // Add new user
                        collaborators[uid] = user;
                        _collaborators$.next(collaborators);
                    });

                    pusherChannel.bind("pusher:member_removed", ({ id: uid }) => {
                        // Remove user
                        delete collaborators[uid];
                        _collaborators$.next(collaborators);
                    });

                    pusherChannel.bind("client-on-slide", ({ slideId }, { user_id: uid }) => {
                        // Updating the slide id value on the collaborator (can be null)
                        collaborators[uid].slideId = slideId;
                        _collaborators$.next(collaborators);
                    });

                    // Filling the initial state
                    pusherChannel.members.each(({ id: uid, info: user }) => {
                        collaborators[uid] = user;
                    });
                    _collaborators$.next(collaborators);

                    currentPresentationId = presentation.id;

                    // Force updating current slide (if present)
                    onSlideChanged(this.dataService.selection.slide);
                }
            } catch (err) {
                logger.error(err, "_getCollaboratorsObservable() onPresentationChanged() failed");
            }
        };

        this.dataService.selection.on("change:presentation", selection => onPresentationChanged(selection.presentation));
        this.dataService.selection.on("change:slide", selection => onSlideChanged(selection.slide));
        this.dataService.selection.on("firstSlideSet", () => onSlideChanged(this.dataService.selection.slide));

        // Fetching the initial state
        onPresentationChanged(this.dataService.selection.presentation);

        return collaborators$;
    }
}
